import { useState, useEffect } from 'react';
import { Seo } from '../components/Seo';
import { Link } from 'react-router-dom';
import { Car, Heart, Building2, Shield, CheckCircle, ArrowRight, Home as HomeIcon, Bike, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getServices } from '../utils/api';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        // Log error for debugging - TODO: Replace with proper error tracking service in production
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - fetch once on component mount only
  const iconMap = {
    Car,
    Heart,
    Building2,
    Home: HomeIcon,
    Bike,
    FileText
  };

  const additionalBenefits = [
    "Independent agent - multiple carrier options",
    "Personalized coverage for Central Oregon",
    "Local agent familiar with Prineville & Central OR",
    "Expert claims assistance when you need it",
    "Annual policy reviews & adjustments",
    "Competitive rates and bundle discounts"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Insurance Services - Contractor, Business, Home & Auto | Kerry M Holloway Agency, Central Oregon"
        description="Contractor insurance, CCB bonds, general liability, commercial, home, auto, and life insurance in Prineville, Bend, Redmond, and Madras. Compare 20+ carriers with a local Central Oregon agent."
        path="/services"
      />
      {/* Hero Section */}
      <section className="bg-white" style={{ paddingTop: "360px", paddingBottom: "48px" }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6">
            <div className="inline-flex items-center space-x-2 bg-red-50 px-4 py-2 rounded-full">
              <Shield className="h-4 w-4 text-[#D94B5A]" />
              <span className="text-sm font-medium text-[#D94B5A]">Our Services</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F1F1F]">
              Insurance Solutions for Central Oregon
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Serving Prineville, Bend, Redmond, Madras, and throughout Crook County, Deschutes County, and Jefferson County. From contractor and commercial insurance to home, auto, and life—comprehensive coverage that protects what matters most.
            </p>
          </div>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading services...</p>
            </div>
          ) : (
            <div className="space-y-20">
              {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div key={service.id} className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Image */}
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-[300px] sm:h-[350px] lg:h-[400px] object-contain bg-gray-100 group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent" />
                      <div className="absolute bottom-6 left-6">
                        <div className="bg-white p-3 rounded-xl shadow-lg">
                          <Icon className="h-8 w-8 text-[#D94B5A]" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`space-y-6 ${!isEven ? 'lg:order-1' : ''}`}>
                    <div className="inline-flex items-center space-x-2 bg-red-50 px-3 py-1 rounded-full">
                      <Icon className="h-4 w-4 text-[#D94B5A]" />
                      <span className="text-sm font-medium text-[#D94B5A]">Service {index + 1}</span>
                    </div>
                    
                    <h2 className="text-3xl lg:text-4xl font-bold text-[#1F1F1F]">
                      {service.title}
                    </h2>
                    
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="space-y-3">
                      <h3 className="font-semibold text-[#1F1F1F] text-lg">Coverage Includes:</h3>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {service.features.map((feature) => (
                          <div key={feature} className="flex items-start space-x-2">
                            <CheckCircle className="h-5 w-5 text-[#D94B5A] flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <Link to="/quote">
                      <Button size="lg" className="bg-[#D94B5A] hover:bg-[#C43D4D] text-white transition-all duration-300">
                        Get a Quote
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-20 bg-gradient-to-br bg-[#F7F7F7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1F1F1F] mb-4">
                What You Get With Every Policy
              </h2>
              <p className="text-lg text-gray-600">
                Beyond great coverage, we provide exceptional service and support every step of the way.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {additionalBenefits.map((benefit) => (
                <Card key={benefit} className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br bg-[#D94B5A] p-2 rounded-lg flex-shrink-0">
                        <CheckCircle className="h-5 w-5 text-white" />
                      </div>
                      <p className="text-gray-700 font-medium pt-1">{benefit}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1F1F1F]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-3xl lg:text-5xl font-bold text-white">
              Ready to Protect Your Business or Family?
            </h2>
            <p className="text-xl text-gray-300">
              Get a free insurance quote for your home, auto, business, or contracting operation. Serving Central Oregon since 2009.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link to="/quote">
                <Button size="lg" className="bg-[#D94B5A] hover:bg-[#C43D4D] text-white transition-all duration-300 text-lg px-8">
                  Request a Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1F1F1F] transition-all duration-300 text-lg px-8">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
